import { api } from './api';
import { LoginRequest, LoginResponse, User, ApiError } from '../types';

// Response type for password operations
interface PasswordOperationResult {
  success: boolean;
  error?: ApiError;
}

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';

// Authorization header authentication with token storage
// Cookie認証は廃止され、sessionStorage でトークンを管理
class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // Backend returns access_token and refresh_token in response body
    const response = await api.post<any>('/auth/login', credentials);

    // Store access token in sessionStorage
    if (response.access_token) {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, response.access_token);

      // Calculate and store expiry time
      const expiryTime = Date.now() + (response.expires_in * 1000);
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    }

    // Store refresh token in sessionStorage
    if (response.refresh_token) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);
    }

    // CSRF token は不要（Cookie認証廃止により）

    // Fetch user info (Authorization header is automatically sent)
    const user = await this.getCurrentUser();

    return {
      user,
      tokens: {
        accessToken: response.access_token || '',
        refreshToken: response.refresh_token || '',
        expiresIn: response.expires_in || 3600,
      },
    };
  }

  async logout(): Promise<void> {
    try {
      // Backend invalidates the token
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local token storage
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    }
  }

  async getCurrentUser(): Promise<User> {
    // Authorization header is automatically sent via api interceptor
    const response = await api.get<any>('/auth/me');

    // Map API response to User type
    // API returns: { id, email, name, tenant_id, roles: [], groups: [] }
    // User type expects: { id, email, fullName, tenantId, role, roles, ... }
    const user: User = {
      id: response.id,
      email: response.email,
      fullName: response.name || response.fullName || '',
      display_name: response.display_name,
      role: response.roles?.[0] || response.role || 'user',  // Take first role or default to 'user'
      roles: response.roles || [],
      tenantId: response.tenant_id || response.tenantId || '',
      tenantCode: response.tenant_code || response.tenantCode || '',
      isActive: response.is_active ?? response.isActive ?? true,
      createdAt: response.created_at || response.createdAt || new Date().toISOString(),
      updatedAt: response.updated_at || response.updatedAt || new Date().toISOString(),
      groups: response.groups || [],
      permissions: response.permissions || [],
      deniedPermissions: response.denied_permissions || response.deniedPermissions || [],
      allowedTenants: response.allowed_tenants || response.allowedTenants || [],
      tenantSettings: response.tenant_settings || response.tenantSettings,
    };

    return user;
  }

  async refreshToken(): Promise<LoginResponse> {
    // Get refresh token from sessionStorage and send in request body
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<any>('/auth/refresh', {
      refresh_token: refreshToken
    });

    // Store new access token
    if (response.access_token) {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, response.access_token);

      // Calculate and store expiry time
      const expiryTime = Date.now() + (response.expires_in * 1000);
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    }

    // Store new refresh token if rotated
    if (response.refresh_token) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);
    }

    // Fetch updated user info
    const user = await this.getCurrentUser();

    return {
      user,
      tokens: {
        accessToken: response.access_token || '',
        refreshToken: response.refresh_token || '',
        expiresIn: response.expires_in || 3600,
      },
    };
  }

  async requestPasswordReset(data: { email: string; tenantCode: string }): Promise<PasswordOperationResult> {
    try {
      await api.post('/auth/forgot-password', data);
      return { success: true };
    } catch (error) {
      const apiError = error as ApiError;
      console.error('Password reset request failed:', apiError);
      return {
        success: false,
        error: apiError
      };
    }
  }

  async confirmPasswordReset(data: { token: string; password: string; confirmPassword: string }): Promise<PasswordOperationResult> {
    try {
      await api.post('/auth/reset-password', {
        token: data.token,
        newPassword: data.password,
        confirmPassword: data.confirmPassword,
      });
      return { success: true };
    } catch (error) {
      const apiError = error as ApiError;
      console.error('Password reset confirmation failed:', apiError);
      return {
        success: false,
        error: apiError
      };
    }
  }

  async changePassword(data: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<PasswordOperationResult> {
    try {
      await api.post('/auth/change-password', data);
      return { success: true };
    } catch (error) {
      const apiError = error as ApiError;
      console.error('Password change failed:', apiError);
      return {
        success: false,
        error: apiError
      };
    }
  }

  // Check authentication by attempting to get current user
  // HttpOnly cookies don't allow JS access, so we validate via API call
  async isAuthenticated(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  // Get access token from sessionStorage
  getAuthToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  async validateSession(): Promise<boolean> {
    // Check if we have a valid session by getting current user
    try {
      await this.getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }
}

export const authService = new AuthService();
